// Import lib
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Search } from "@mui/icons-material";
// import * as FullStory from "@fullstory/browser";
import {
  Header as GravityHeader,
  LeftNavigationComponent as LeftNavigation,
} from "@gravity/tools";
import {
  updateStateSubject,
  createStateSubscription,
  destroyStateSubscription,
  // eslint-disable-next-line import/no-unresolved
} from "@gartner/genie-messenger-util";
// eslint-disable-next-line import/no-unresolved
import { logout, redirectToLogin } from "@gartner/login";
// eslint-disable-next-line import/no-unresolved
import { Autocomplete } from "@gartner/search";

// Import components
import { Notification } from "@genie-ui/react";

// Import utils
import { ACTIS_REPORTING } from "@utils/actisReporting";
import { getPageNameByPath } from "@utils/pageHelper";
import debounce from "@utils/debounce";
import {
  getCurrentEnvForAppLauncher,
  getGCOMEnv,
  getPageFromUrlForActisTracking,
  // isProd,
} from "@utils/Utilities";
import { Constants } from "@constants/Constants";
import { ActisReportingConstants } from "@constants/ActisReportingConstants";

// Import hooks
import { useLeftNavigation } from "@hooks";

// Import styles
import "./header.scss";

const Header = ({ currentUser, searchData }) => {
  // Props
  const { role, profileId, shell } = currentUser || {};

  // Hooks
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { isLeftNavigationAvailable } = useLeftNavigation(role);
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(max-width: 1023px)");
  const [enableSearch, setEnableSearch] = useState(false);
  const [enableMobileSearch, setEnableMobileSearch] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [triggerNotification, setTriggerNotification] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [customAnchorEl, setCustomAnchorEl] = useState(null);
  const pageLoad = useRef(true);
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Required Variables
  const urlProfileId = searchParams.get("profileId");
  const open = Boolean(anchorEl);
  const userName = `${currentUser?.encryptedFirstName}${
    currentUser?.encryptedLastName ? " " + currentUser?.encryptedLastName : ""
  }`;
  const userId = currentUser?.userId;
  const orgId = currentUser?.orgId;
  const isGartnerUser =
    currentUser?.email?.toString().includes("@gartner.com") ||
    currentUser?.email?.toString().includes("@test.com");
  const userRole = currentUser?.role;
  // const isProdEnv = isProd();

  // Side Effects

  // if (isProdEnv) {
  //   FullStory.identify(userId, {
  //     displayName: userName,
  //     orgId: orgId,
  //     isGartnerUser: isGartnerUser,
  //     role: userRole,
  //   });
  // }
  const debouncedUpdateStateSubject = useCallback(
    debounce(updateStateSubject, 200),
    []
  );

  // Side Effects
  useEffect(() => {
    // if (isProdEnv) {
    //   FullStory.setVars("page", {
    //     pageName: pathname,
    //   });
    // }
    if (Constants.COMMON.END_USER_ROLE.includes(role)) {
      // Search in enabled for all pages for an end user
      setEnableSearch(true);
    } else {
      setEnableSearch(false);
    }
    if (currentUser.statusCode === 200) {
      if (
        window.location.href.indexOf(Constants.ROUTES.ACCESS_DENIED) < 0 &&
        (Constants.ROLE.indexOf(role) < 0 ||
          currentUser?.orgVerification < 1) &&
        pathname.indexOf(Constants.ROUTES.GUEST_PROFILE) < 0
      ) {
        navigate(Constants.ROUTES.ACCESS_DENIED);
      } else if (
        window.location.href.indexOf(Constants.ROUTES.ACCESS_DENIED) > -1 &&
        Constants.ROLE.indexOf(role) > -1
      ) {
        navigate(Constants.ROUTES.HOMEPAGE);
      }
    }
    const subscriptionNotificationDetails = createStateSubscription(
      "triggerNotification",
      (data) => {
        setTriggerNotification(data?.trigger);
        return data;
      }
    );
    return () => {
      destroyStateSubscription(subscriptionNotificationDetails);
    };
  }, [pathname]);

  useEffect(() => {
    if (pageLoad.current) {
      pageLoad.current = false;
      return;
    } else {
      debouncedUpdateStateSubject("triggerNotification", { trigger: true });
    }
  }, [search, pathname]);

  useEffect(() => {
    if (searchData?.active === false) {
      setEnableMobileSearch(false);
    }
  }, [searchData?.active]);

  useEffect(() => {
    if (triggerNotification) {
      setTriggerNotification(false);
      updateStateSubject("triggerNotification", { trigger: false });
    }
  }, [triggerNotification]);

  useEffect(() => {
    const handlePageShow = (event) => {
      const loggedOut = localStorage.getItem(Constants.LOGOUT_EVENT_KEY) === "true";
      if (loggedOut) {
        redirectToLogin();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  // Event Handlers
  const handleNotificationCountCallback = (value) => {
    setUnreadNotificationCount(value);
  };

  const onClose = () => {
    setCustomAnchorEl(null);
  };

  const handleAssessorSecondaryLogoClick = () => {
    ACTIS_REPORTING.ASSESSOR_SECONDARY_LOGO.push({
      label:
        ActisReportingConstants.ASSESSOR.LABELS.SECONDARY_LOGO_CLICK.replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.ROLE,
          role
        ),
      action: ActisReportingConstants.ASSESSOR.ACTIONS.SECONDARY_LOGO_CLICK,
    });
  };

  const handleSecondaryLogoClick = (e) => {
    if (role === Constants.COMMON.ASSESSOR) {
      handleAssessorSecondaryLogoClick();
      navigate(Constants.ROUTES.TEAM_ACTIVITY);
      return;
    }

    currentUser?.userId &&
      ACTIS_REPORTING.SECONDARY_LOGO.push({
        label: `${getPageFromUrlForActisTracking()}|${
          ActisReportingConstants.SECONDARY_LOGO
        }|${currentUser.orgId}|${currentUser.companyName}`,
        action: ActisReportingConstants.SECONDARY_LOGO,
      });

    if (pathname.includes(Constants.ROUTES.GUEST_PROFILE) && !role) {
      e.stopPropagation();
      return;
    }
    const navigateUrl =
      role === Constants.COMMON.VENDOR && profileId
        ? `${Constants.ROUTES.RISK_PROFILE}${
            Constants.ROUTES.RISK_PROFILE_PARAMS
          }${profileId}${
            currentUser?.shell ? Constants.ROUTES.RISK_PROFILE_PARAMS_SHELL : ""
          }`
        : Constants.ROUTES.HOMEPAGE;

    if (
      pathname.includes("/profile-setup") &&
      !shell &&
      profileId !== urlProfileId
    ) {
      updateStateSubject("secondaryLogoClick", { e, shell });
    } else if (pathname.includes("/profile-setup")) {
      updateStateSubject("secondaryLogoClick", { e, shell: true });
    } else if (
      !pathname.includes(Constants.ROUTES.EDIT) &&
      !pathname.includes(Constants.ROUTES.VENDOR_ONBOARDING) &&
      !pathname.includes(Constants.ROUTES.PROFILE_SETUP)
    ) {
      navigate(navigateUrl);
    } else {
      updateStateSubject("secondaryLogoClick", e);
    }
  };

  const handleAppLauncherExpand = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        ActisReportingConstants.APP_LAUNCHER.LABELS.APP_LAUNCHER_EXPANDED.replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ),
      action:
        ActisReportingConstants.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_EXPANDED,
    });
  };

  const handleAppLauncherCollapse = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        ActisReportingConstants.APP_LAUNCHER.LABELS.APP_LAUNCHER_COLLAPSED.replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ),
      action:
        ActisReportingConstants.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_COLLAPSED,
    });
  };

  const handleAppLauncherToolClicked = (...args) => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        ActisReportingConstants.APP_LAUNCHER.LABELS.APP_LAUNCHER_TOOL_CLICKED.replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ).replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.TOOL_NAME,
          args[1]
        ),
      action:
        ActisReportingConstants.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_TOOL_CLICKED,
    });
  };

  const handleAppLauncherSeeAllTools = () => {
    ACTIS_REPORTING.APP_LAUNCHER.push({
      label:
        ActisReportingConstants.APP_LAUNCHER.LABELS.APP_LAUNCHER_SEE_ALL_TOOLS.replace(
          ActisReportingConstants.COMMON_PLACEHOLDERS.PAGE_NAME,
          getPageNameByPath()
        ),
      action:
        ActisReportingConstants.APP_LAUNCHER.ACTIONS.APP_LAUNCHER_SEE_ALL_TOOLS,
    });
  };

  const handleNavMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    updateStateSubject("logout", true);
    if (logout) {
      logout();
    }
  };

  const onMobileSearchClick = () => {
    setEnableMobileSearch(!enableMobileSearch);
    updateStateSubject("search", { active: true });
  };

  const handleAdminPanelActis = () => {
    ACTIS_REPORTING.PROFILE_MANAGE_TEAM.push({
      label: ActisReportingConstants.PROFILE_MANAGE_TEAM_LABEL,
      action: ActisReportingConstants.PROFILE_MANAGE_TEAM_ACTION,
    });
  };

  const handleAdminPanel = () => {
    handleNavMenuClose();
    handleAdminPanelActis();
    searchParams.set(Constants.ROUTES.TEAM_DRAWER_PARAM, true);
    setSearchParams(searchParams);
  };

  const handleSettings = () => {
    handleNavMenuClose();
    navigate(Constants.ROUTES.SETTINGS);
  };

  const onProfilePreferencesClick = () => {
    handleNavMenuClose();
    updateStateSubject("profilepreferences", {
      redirectProfilePreferences: true,
    });
  };

  const onHelpCTAClick = () => {
    window.open(Constants.HELP.URL, "_blank");
    ACTIS_REPORTING.HELP.push({
      label: `${ActisReportingConstants.GLOBAL_HEADER}|${getPageNameByPath()}|${
        ActisReportingConstants.HELP_CLICKED
      }|${currentUser.orgId}|${currentUser.companyName}`,
      action: ActisReportingConstants.USER_CLICKS_HELP,
    });
  };

  const searchCallActis = (searchActisData) => {
    ACTIS_REPORTING.SEARCH.push({
      label: `${ActisReportingConstants.GLOBAL_HEADER}|${getPageNameByPath()}|${
        searchActisData.searchTerm
      }|${ActisReportingConstants.IN_MY_ORGANIZATION_STATUS}:${
        searchActisData.inMyOrganization
      }|${ActisReportingConstants.OUTSIDE_MY_ORGANIZATION_STATUS}:${
        searchActisData.outsideMyOrganization
      }|${currentUser.orgId}|${currentUser.companyName}`,
      action: ActisReportingConstants.SEARCH_ACTION,
    });
  };

  const searchDropdownLastOptionClick = () => {
    ACTIS_REPORTING.ADD_UNKNOWN_VENDOR_CTA.push({
      label:
        ActisReportingConstants.ADD_UNKNOWN_VENDOR.LABELS
          .ADD_UNKNOWN_VENDOR_CTA_GLOBAL_SEARCH,
      action:
        ActisReportingConstants.ADD_UNKNOWN_VENDOR.ACTIONS
          .ADD_UNKNOWN_VENDOR_CTA_GLOBAL_SEARCH,
    });
  };

  const handleBellIconClickActis = () => {
    const { NOTIFICATION } = ActisReportingConstants;
    const { LABELS, ACTIONS, PLACEHOLDER } = NOTIFICATION;
    const label = LABELS.NOTIFICATION_BELL.replace(
      PLACEHOLDER.END_USER_VENDOR_NAME_PLACEHOLDER,
      currentUser.companyName
    ).replace(PLACEHOLDER.END_USER_VENDOR_ID_PLACEHOLDER, currentUser.orgId);
    ACTIS_REPORTING.NOTIFICATION_BELL.push({
      label,
      action: ACTIONS.NOTIFICATION_BELL,
    });
  };

  const handleNotificationCtaClickActis = (item, actionItem) => {
    const { NOTIFICATION } = ActisReportingConstants;
    const { LABELS, ACTIONS, PLACEHOLDER } = NOTIFICATION;
    const label = LABELS.NOTIFICATION_CTA.replace(
      PLACEHOLDER.END_USER_VENDOR_NAME_PLACEHOLDER,
      currentUser.companyName
    )
      .replace(PLACEHOLDER.END_USER_VENDOR_ID_PLACEHOLDER, currentUser.orgId)
      .replace(PLACEHOLDER.NOTIFICATION_HEADER_TEXT, item?.title || "")
      .replace(PLACEHOLDER.CTA_LABEL, actionItem?.ctaLabel || "");
    ACTIS_REPORTING.NOTIFICATION_CTA.push({
      label,
      action: ACTIONS.NOTIFICATION_CTA,
    });
  };

  const navigateFromApp = (url) => {
    navigate(url.replace(Constants.ROUTES.BASE_URL, ""));
  };

  const handleAppLogoClick = () => {
    window.open(getGCOMEnv(), "_blank");

    currentUser?.userId &&
      ACTIS_REPORTING.PRIMARY_LOGO.push({
        label: `${getPageFromUrlForActisTracking()}|${
          ActisReportingConstants.PRIMARY_LOGO
        }|${currentUser.orgId}|${currentUser.companyName}`,
        action:
          ActisReportingConstants.PRIMARY_LOGO +
          ActisReportingConstants.CLICKED,
      });
  };

  const onNotificationClick = (event) => {
    setCustomAnchorEl(event.target);
  };

  const handleSecondaryLogoClickLogic = () => {
    // Creates a synthetic event to mimic the original <span> element's data-url-value attribute
    // This is needed because GravityHeader doesn't support passing data attributes on the app title,
    // but downstream code (e.g., VendorOnboardingProfileSetup) expects event.target.getAttribute('data-url-value')
    const createSyntheticEventWithDataUrl = (dataUrlValue) => {
      return {
        target: {
          getAttribute: (attr) => {
            if (attr === "data-url-value") {
              return dataUrlValue;
            }
            return null;
          },
        },
        stopPropagation: () => {},
        preventDefault: () => {},
      };
    };
    const dataUrlValue =
      role === Constants.COMMON.VENDOR && profileId
        ? `${Constants.ROUTES.RISK_PROFILE}${
            Constants.ROUTES.RISK_PROFILE_PARAMS
          }${profileId}${
            currentUser?.shell ? Constants.ROUTES.RISK_PROFILE_PARAMS_SHELL : ""
          }`
        : Constants.ROUTES.HOMEPAGE;
    const syntheticEvent = createSyntheticEventWithDataUrl(dataUrlValue);
    handleSecondaryLogoClick(syntheticEvent);
  };

  const onItemClick = (e, item) => {
    switch (item) {
      case "logo":
        handleAppLogoClick();
        break;
      case "appTitle":
        handleSecondaryLogoClickLogic();
        break;
      case "help":
        onHelpCTAClick();
        break;
      case "notifications":
        onNotificationClick(e);
        break;
      default:
        break;
    }
  };
  const navigateToRoute = (route) => () => navigate(route);

  const { ACTIONS, LABELS } = ActisReportingConstants.SITE_NAVIGATION;

  const onHomeClick = () => {
    ACTIS_REPORTING.HOME.push({
      label: LABELS.VIEW_HOMEPAGE,
      action: ACTIONS.HOMEPAGE,
    });
    navigate(`${Constants.ROUTES.HOMEPAGE}`);
  };

  const onAllInsightsAlertsClick = () => {
    navigate(`${Constants.ROUTES.ALL_INSIGHTS_ALERTS}`);
  };

  const onBrowseVendorsClick = () => {
    ACTIS_REPORTING.BROWSE_VENDORS.push({
      label: LABELS.VIEW_BROWSE_VENDORS_PAGE,
      action: ACTIONS.BROWSE_VENDORS_VISIT,
    });
    navigate(`${Constants.ROUTES.ALL_VENDORS}`);
  };

  const onImportVendorsClick = () => {
    ACTIS_REPORTING.IMPORT_VENDORS.push({
      label: LABELS.VIEW_IMPORT_VENDORS_PAGE,
      action: ACTIONS.IMPORT_VENDORS_VISIT,
    });
    navigate(`${Constants.ROUTES.IMPORT_VENDORS}`);
  };

  const onAssessmentDashboardClick = () => {
    ACTIS_REPORTING.ASSESSMENT_DASHBOARD.push({
      label: LABELS.VIEW_ASSESSMENT_DASHBOARD,
      action: ACTIONS.ASSESSMENT_DASHBOARD_VISIT,
    });
    navigate(`${Constants.ROUTES.DASHBOARD}`);
  };

  const onManageVendorsClick = () => {
    ACTIS_REPORTING.MANAGE_VENDORS.push({
      label: LABELS.VIEW_MANAGE_VENDORS_PAGE,
      action: ACTIONS.MANAGE_VENDORS_VISIT,
    });
    navigate(`${Constants.ROUTES.MANAGE_VENDORS}`);
  };

  const onTeamActivityClick = () => {
    ACTIS_REPORTING.TEAM_ACTIVITY.push({
      label: LABELS.VIEW_TEAM_ACTIVITY_PAGE,
      action: ACTIONS.TEAM_ACTIVITY_VISIT,
    });
    navigate(`${Constants.ROUTES.TEAM_ACTIVITY}`);
  };

  // JSX Variables
  let avatarChars = currentUser?.firstName?.charAt(0);
  if (currentUser?.lastName?.charAt(0)) {
    avatarChars += currentUser?.lastName?.charAt(0);
  }
  const hasAdminPanelAccess = currentUser?.roles?.includes(
    Constants.COMMON.ADMIN_PANEL
  );
  const userMenuListOptions = [
    ...(hasAdminPanelAccess
      ? [
          {
            id: "your-team",
            label: Constants.HEADER.YOUR_TEAM,
            onClick: handleAdminPanel,
          },
        ]
      : []),
    ...(Constants.COMMON.END_USER_ROLE.includes(role)
      ? [
          {
            id: "notification-manager",
            label: Constants.HEADER.NOTIFICATION_MANAGER,
            onClick: handleSettings,
          },
        ]
      : []),
    ...(role === Constants.COMMON.VENDOR && !currentUser?.shell
      ? [
          {
            id: "profile-preferences",
            label: Constants.HEADER.PROFILE_PREFERENCES,
            onClick: onProfilePreferencesClick,
          },
        ]
      : []),
    {
      id: "sign-out",
      label: Constants.HEADER.SIGN_OUT,
      onClick: handleLogout,
    },
  ];
  const leftNavigationPrimaryItems = [
    {
      icon: "HOME",
      id: "home",
      label: "Home",
      type: "INTERNAL_LINK",
      onClick: onHomeClick,
      isHighlighted: [Constants.ROUTES.HOMEPAGE].includes(pathname),
    },
    {
      icon: "VENDORS_2",
      id: "vendors",
      label: "Vendors",
      type: "SECONDARY_NAV_LINK",
      isHighlighted: [
        Constants.ROUTES.ALL_VENDORS,
        Constants.ROUTES.IMPORT_VENDORS,
        Constants.ROUTES.ALL_INSIGHTS_ALERTS,
      ].includes(pathname),
      subNavItems: [
        {
          id: "browser-vendors",
          label: "Browse Vendors",
          onClick: onBrowseVendorsClick,
          isHighlighted: [Constants.ROUTES.ALL_VENDORS].includes(pathname),
        },
        {
          id: "all-insights-alerts",
          label: "All Insights and Alerts",
          onClick: onAllInsightsAlertsClick,
          isHighlighted: [Constants.ROUTES.ALL_INSIGHTS_ALERTS].includes(pathname),
        },
        {
          id: "import-vendors",
          label: "Import Vendors",
          onClick: onImportVendorsClick,
          isHighlighted: [Constants.ROUTES.IMPORT_VENDORS].includes(pathname),
        },
      ],
    },
    {
      icon: "ANALYTICS",
      id: "assessments",
      label: "Assessments",
      type: "SECONDARY_NAV_LINK",
      isHighlighted: [
        Constants.ROUTES.DASHBOARD,
        Constants.ROUTES.MANAGE_VENDORS,
      ].includes(pathname),
      subNavItems: [
        {
          id: "dashboard",
          label: "Dashboard",
          onClick: onAssessmentDashboardClick,
          isHighlighted: [Constants.ROUTES.DASHBOARD].includes(pathname),
        },
        {
          id: "manage-vendors",
          label: "Manage Portfolio",
          onClick: onManageVendorsClick,
          isHighlighted: [Constants.ROUTES.MANAGE_VENDORS].includes(pathname),
        },
      ],
    },
    {
      icon: "CHECKLIST",
      id: "team-activity",
      label: "Team Activity",
      type: "INTERNAL_LINK",
      onClick: onTeamActivityClick,
      isHighlighted: [Constants.ROUTES.TEAM_ACTIVITY].includes(pathname),
    },
  ];
  const showNotifications =
    Constants.COMMON.END_USER_ROLE.includes(role) ||
    Constants.COMMON.VENDOR === role;

  return (
    <div className="App">
      {enableMobileSearch && isTablet && (
        <Autocomplete
          setFocus={searchData?.active}
          placeholder={Constants.SEARCH.PLACEHOLDER}
          lastOptionLabel={Constants.SEARCH.LAST_OPTION_LABEL}
          lastOptionContainerClass="apollo-navbar__search--createVendor"
          searchCallActis={searchCallActis}
          searchDropdownLastOptionClick={searchDropdownLastOptionClick}
          componentName={Constants.COMMON.GLOBAL_SEARCH}
        />
      )}
      <GravityHeader
        className="apollo-c-gravityHeader"
        appName={Constants.HEADER.SECONDARY_LOGO_LABEL}
        userFirstName={currentUser?.firstName || ""}
        userLastName={currentUser?.lastName || ""}
        userEmail={currentUser?.email || ""}
        showAppLauncher={true}
        showHelp={true}
        userMenuListOptions={userMenuListOptions}
        onItemClick={onItemClick}
        isMenuOpen={showMobileNav}
        onMenuClick={(isOpen) => setShowMobileNav(isOpen)}
        appLauncherProps={{
          environment: getCurrentEnvForAppLauncher(),
          preloadModules: true,
          preloadTimeout: 1500,
          onAppLauncherClick: handleAppLauncherExpand,
          onAppLauncherClose: handleAppLauncherCollapse,
          onToolClick: handleAppLauncherToolClicked,
          onSeeAllToolsClick: handleAppLauncherSeeAllTools,
        }}
        showNotifications={showNotifications}
        notificationCount={unreadNotificationCount}
        {...(enableSearch && {
          secondaryComponent: (
            <>
              {!isTablet && (
                <div className="apollo-navbar__search">
                  <Autocomplete
                    width="100%"
                    placeholder={Constants.SEARCH.PLACEHOLDER}
                    lastOptionLabel={Constants.SEARCH.LAST_OPTION_LABEL}
                    lastOptionContainerClass="apollo-navbar__search--createVendor"
                    searchCallActis={searchCallActis}
                    searchDropdownLastOptionClick={
                      searchDropdownLastOptionClick
                    }
                    componentName={Constants.COMMON.GLOBAL_SEARCH}
                    maxWidth={360}
                  />
                </div>
              )}
              {isTablet && (
                <Search
                  onClick={onMobileSearchClick}
                  style={{ color: "#002856", fontSize: 24 }}
                />
              )}
            </>
          ),
        })}
      />
      {isLeftNavigationAvailable && (
        <LeftNavigation
          isMobilePrimaryNavOpen={isMobile && showMobileNav}
          onToggleMobilePrimaryNav={(isOpen) => setShowMobileNav(isOpen)}
          primaryItems={leftNavigationPrimaryItems}
        />
      )}
      {role && role !== Constants.COMMON.ASSESSOR && (
        <Notification
          appKey="apollo-api"
          baseUrl={Constants.API.API_PATH}
          triggerNotification={triggerNotification}
          navigateFromApp={navigateFromApp}
          onOpen={handleBellIconClickActis}
          itemProps={{
            onItemButtonClick: handleNotificationCtaClickActis,
          }}
          customNotificationIcon={true}
          customAnchorEl={customAnchorEl}
          onClose={onClose}
          unreadNotificationCountHandler={handleNotificationCountCallback}
        />
      )}
    </div>
  );
};

export default Header;
